import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Switch } from "./ui/switch";

const KEY = "fc_cookie_prefs";
const EVENT = "fc-open-cookie-settings";
export const openCookieSettings = () => window.dispatchEvent(new Event(EVENT));
const load = () => { try { return JSON.parse(localStorage.getItem(KEY)); } catch { return null; } };

const OPTIONAL = [
  { id: "preferences", label: "Preferences", text: "Remember choices like the welcome bonus." },
  { id: "analytics", label: "Analytics", text: "Help us see how the game is used." },
  { id: "marketing", label: "Marketing", text: "Measure our promotions." },
];

// Cookie banner on first visit + a settings dialog (reopened from the footer).
const CookieConsent = () => {
  const [prefs, setPrefs] = useState(load);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ preferences: true, analytics: false, marketing: false });

  useEffect(() => {
    const show = () => { setDraft({ preferences: true, analytics: false, marketing: false, ...(load() || {}) }); setOpen(true); };
    window.addEventListener(EVENT, show);
    return () => window.removeEventListener(EVENT, show);
  }, []);

  const save = (p) => {
    const v = { essential: true, ...p, saved_at: new Date().toISOString() };
    localStorage.setItem(KEY, JSON.stringify(v));
    setPrefs(v);
    setOpen(false);
  };

  return (
    <>
      {!prefs && !open && (
        <div data-testid="cookie-banner" className="theme-light fixed bottom-3 left-3 right-3 sm:left-auto sm:right-5 sm:bottom-5 sm:max-w-md z-[60] rounded-2xl border border-[#E2E8F0] bg-white p-4 shadow-[0_10px_40px_rgba(15,23,42,0.18)]">
          <div className="flex items-start gap-3">
            <Cookie className="w-5 h-5 text-[#D97706] shrink-0 mt-0.5" />
            <p className="text-sm text-[#334155] leading-snug">We use essential cookies to keep you signed in, and optional ones only if you allow them. <Link to="/cookies" className="underline text-[#0F172A]">Cookie Policy</Link></p>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            <button data-testid="cookie-settings-btn" onClick={openCookieSettings} className="rounded-lg border border-[#CBD5E1] py-2 text-sm font-semibold text-[#0F172A] hover:bg-[#F1F5F9]">Settings</button>
            <button data-testid="cookie-reject-btn" onClick={() => save({ preferences: false, analytics: false, marketing: false })} className="rounded-lg border border-[#CBD5E1] py-2 text-sm font-semibold text-[#0F172A] hover:bg-[#F1F5F9]">Reject</button>
            <button data-testid="cookie-accept-btn" onClick={() => save({ preferences: true, analytics: true, marketing: true })} className="btn-green rounded-lg py-2 text-sm">Accept all</button>
          </div>
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="cookie-settings-modal" className="theme-light bg-white text-[#0F172A] w-[calc(100%-2rem)] max-w-sm p-6 rounded-2xl border border-[#E2E8F0]">
          <DialogTitle className="font-display font-extrabold uppercase text-2xl">Cookie settings</DialogTitle>
          <DialogDescription className="text-sm text-[#64748B]">Choose which optional cookies we can use.</DialogDescription>
          <div className="mt-3 divide-y divide-[#E2E8F0]">
            <div className="py-3 flex items-center justify-between gap-3">
              <div><div className="font-semibold text-sm">Essential</div><div className="text-xs text-[#64748B]">Sign-in and security. Always on.</div></div>
              <Switch checked disabled data-testid="cookie-toggle-essential" />
            </div>
            {OPTIONAL.map((o) => (
              <div key={o.id} className="py-3 flex items-center justify-between gap-3">
                <div><div className="font-semibold text-sm">{o.label}</div><div className="text-xs text-[#64748B]">{o.text}</div></div>
                <Switch data-testid={`cookie-toggle-${o.id}`} checked={!!draft[o.id]} onCheckedChange={(v) => setDraft((d) => ({ ...d, [o.id]: v }))} />
              </div>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <button data-testid="cookie-save-btn" onClick={() => save(draft)} className="rounded-lg border border-[#CBD5E1] py-2.5 text-sm font-semibold hover:bg-[#F1F5F9]">Save choices</button>
            <button data-testid="cookie-accept-all-btn" onClick={() => save({ preferences: true, analytics: true, marketing: true })} className="btn-green rounded-lg py-2.5 text-sm">Accept all</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;
